import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageBySlug, getHomePage } from '../../../contentful/queries';
import { mapPageSections, getFieldValue } from '../../../contentful/mappers';
import SectionRenderer from '../../sections/SectionRenderer/SectionRenderer';
import './DynamicPage.css';

function DynamicPage({ initialHomeData = null, initialError = null }) {
  const location = useLocation();
  // Get slug from pathname, removing leading slash
  const slug = location.pathname === '/' ? null : location.pathname.slice(1);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll to top when navigating to home page
  useEffect(() => {
    if (!slug || slug === 'home') {
      // Scroll to top instantly when navigating to home
      window.scrollTo({
        top: 0,
        behavior: 'instant'
      });
    }
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    // Use initial data for home if provided
    if ((!slug || slug === 'home') && initialHomeData) {
      setPage(initialHomeData);
      setError(initialError || null);
      setLoading(false);
      return;
    } else if ((!slug || slug === 'home') && initialError) {
      setError(initialError);
      setLoading(false);
      return;
    }

    async function loadPage() {
      try {
        setLoading(true);
        setError(null);
        if (!process.env.REACT_APP_CONTENTFUL_SPACE_ID || !process.env.REACT_APP_CONTENTFUL_DELIVERY_TOKEN) {
          setError('Contentful not configured. Please add environment variables.');
          setLoading(false);
          return;
        }

        let pageData;
        if (!slug || slug === 'home') {
          pageData = await getHomePage();
        } else {
          pageData = await getPageBySlug(slug);
        }
        if (cancelled) return;
        if (!pageData) {
          setError('Page not found');
          setLoading(false);
          return;
        }
        setPage(pageData);
      } catch (err) {
        if (!cancelled) {
          setError(`Failed to load page: ${err.message || 'Unknown error'}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPage();
    return () => { cancelled = true; };
  }, [slug, initialHomeData, initialError]);

  // Normalize path: treat '/' and '/home' as the same
  const normalizePath = (path) => {
    if (!path || path === '/' || path === '/home') {
      return '/';
    }
    return path;
  };

  // Handle hash-based scrolling to sections
  useEffect(() => {
    if (loading || !page) return;

    const handleHashScroll = () => {
      const hash = window.location.hash.slice(1); // Remove the #
      if (hash) {
        // Wait for sections to render
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            const headerOffset = 80; // Account for fixed header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 200);
      }
    };

    // Scroll on initial load if hash exists
    handleHashScroll();

    // Also handle hash changes
    window.addEventListener('hashchange', handleHashScroll);

    return () => {
      window.removeEventListener('hashchange', handleHashScroll);
    };
  }, [page, loading]); // Re-run when page changes

  // Handle clicks on internal links to prevent full page reloads
  useEffect(() => {
    if (loading || !page) return;

    const handleLinkClick = (e) => {
      const anchor = e.target.closest('a');
      if (!anchor) return;

      // Skip if it's an external link (has target="_blank" or starts with http/https/mailto)
      const href = anchor.getAttribute('href');
      if (!href) return;
      
      if (anchor.target === '_blank' || 
          href.startsWith('http://') || 
          href.startsWith('https://') || 
          href.startsWith('mailto:') ||
          href.startsWith('tel:')) {
        return; // Allow external links to work normally
      }

      // Only handle relative/internal links (starting with / or #)
      if (!href.startsWith('/') && !href.startsWith('#')) {
        return;
      }

      // Parse the href to get path and hash
      let path = href;
      let hash = null;
      
      if (href.startsWith('#')) {
        // Hash-only link (same page anchor)
        path = location.pathname;
        hash = href.slice(1);
      } else if (href.includes('#')) {
        const parts = href.split('#');
        path = parts[0] || '/';
        hash = parts[1];
      }

      // Normalize paths
      const currentPath = normalizePath(location.pathname);
      const targetPath = normalizePath(path);

      // Check if it's the same page
      if (currentPath === targetPath) {
        e.preventDefault();
        e.stopPropagation(); // Prevent React Router from handling it
        
        if (hash) {
          // Update URL hash without reload
          window.history.pushState(null, '', `${location.pathname}#${hash}`);
          
          // Scroll to the element
          setTimeout(() => {
            const element = document.getElementById(hash);
            if (element) {
              const headerOffset = 80;
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          }, 100);
        } else {
          // No hash, just scroll to top
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    };

    // Add click listener to document (use capture phase to intercept before React Router)
    document.addEventListener('click', handleLinkClick, true);

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [page, loading, location.pathname]);

  if (loading) {
    return <div className="page-loading">Loading...</div>;
  }
  if (error || !page) {
    return (
      <div className="page-error">
        <h1>Page Not Found</h1>
        <p>{error || 'The page you are looking for does not exist.'}</p>
      </div>
    );
  }
  const sections = mapPageSections(page);
  const pageTitle = getFieldValue(page, 'title');

  // turning this off for now since we never really want to see this
  const shouldShowPageTitle = false;

  if (sections.length === 0) {
    return (
      <div className="dynamic-page">
        {pageTitle && <h1 className="page-title">{pageTitle}</h1>}
        <div className="page-error" style={{ margin: '2rem', padding: '2rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
          <p>No sections found on this page.</p>
          <p style={{ fontSize: '0.9rem', color: '#856404', marginTop: '0.5rem' }}>
            Make sure your page in Contentful has sections added to it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dynamic-page">
      {shouldShowPageTitle && <h1 className="page-title">{pageTitle}</h1>}
      {sections.map((section, index) => {
        return <SectionRenderer key={section.id} section={section} isFirst={index === 0} />;
      })}
    </div>
  );
}

export default DynamicPage;

